import { useState, useEffect, useContext } from "react";
import { Form, Button, Container, Row, Col, Card } from "react-bootstrap";
import { Notyf } from "notyf";
import { useNavigate, Link } from "react-router-dom";
import UserContext from "../context/UserContext";

function Login() {
  const notyf = new Notyf();
  const navigate = useNavigate();
  const { fetchUserData } = useContext(UserContext);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [isButtonActive, setIsButtonActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    if (formData.email === "" || formData.password === "") {
      setIsButtonActive(false);
    } else {
      setIsButtonActive(true);
    }
  }, [formData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    fetch(`${process.env.REACT_APP_API_BASE_URL}/users/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (!response.ok) {
          notyf.error("Login failed. Please check your credentials.");
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        localStorage.setItem("token", data.access);
        fetchUserData(data.access);
        notyf.success("Login successful!");
        navigate("/products");
      })
      .catch((error) => {
        console.error("Error logging in:", error);
        notyf.error("An error occurred while logging in.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="auth-page-wrapper">
      {/* Auth Hero Section */}
      <div className="auth-hero-section">
        <Container>
          <div className="text-center py-5">
            <div className="auth-icon-container mx-auto mb-4">
              <i className="fas fa-sign-in-alt text-white"></i>
            </div>
            <h1 className="display-4 fw-bold text-white mb-3">Welcome Back!</h1>
            <p className="lead text-white-50 mb-4">
              Sign in to your FreshMart account and continue shopping
            </p>
            <div className="hero-divider mx-auto"></div>
          </div>
        </Container>
      </div>

      {/* Login Form Section */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6} xl={5}>
            <Card className="login-card border-0 shadow-lg">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <div className="form-icon-container mx-auto mb-3">
                    <i className="fas fa-user-circle"></i>
                  </div>
                  <h3 className="fw-bold text-dark mb-2">Sign In</h3>
                  <p className="text-muted">
                    Enter your credentials to access your account
                  </p>
                </div>

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark mb-2">
                      <i className="fas fa-envelope me-2 text-success"></i>
                      Email Address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      className="modern-input"
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e9ecef",
                        padding: "12px 16px",
                        fontSize: "1rem",
                        transition: "all 0.3s ease",
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark mb-2">
                      <i className="fas fa-lock me-2 text-success"></i>
                      Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="modern-input"
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e9ecef",
                        padding: "12px 16px",
                        fontSize: "1rem",
                        transition: "all 0.3s ease",
                      }}
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <Form.Check
                      type="checkbox"
                      label="Remember me"
                      className="text-muted"
                    />
                    <Link
                      to="#"
                      className="text-success text-decoration-none fw-semibold"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  <div className="d-grid mb-4">
                    <Button
                      type="submit"
                      disabled={!isButtonActive || isLoading}
                      className="login-btn fw-bold py-3"
                      style={{
                        borderRadius: "12px",
                        fontSize: "1.1rem",
                        background: isButtonActive
                          ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                          : "#6c757d",
                        border: "none",
                        boxShadow: isButtonActive
                          ? "0 8px 25px rgba(40, 167, 69, 0.3)"
                          : "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {isLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin me-2"></i>
                          Signing In...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sign-in-alt me-2"></i>
                          Sign In
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="text-center">
                    <p className="text-muted mb-0">
                      Don't have an account?{" "}
                      <Link
                        to="/register"
                        className="text-success fw-semibold text-decoration-none"
                      >
                        Create Account
                      </Link>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Enhanced Custom Styles */}
      <style jsx>{`
        .auth-page-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }

        .auth-hero-section {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          position: relative;
          overflow: hidden;
        }

        .auth-hero-section::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="authPattern" width="25" height="25" patternUnits="userSpaceOnUse"><circle cx="12.5" cy="12.5" r="1" fill="white" opacity="0.1"/><circle cx="6" cy="19" r="0.5" fill="white" opacity="0.15"/><circle cx="19" cy="6" r="0.5" fill="white" opacity="0.15"/></pattern></defs><rect width="100" height="100" fill="url(%23authPattern)"/></svg>');
          pointer-events: none;
        }

        .auth-icon-container {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 100px;
          height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(10px);
        }

        .auth-icon-container i {
          font-size: 3rem;
        }

        .hero-divider {
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, transparent, white, transparent);
          border-radius: 2px;
        }

        .login-card {
          border-radius: 25px !important;
          overflow: hidden;
          backdrop-filter: blur(10px);
        }

        .form-icon-container {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          border-radius: 50%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .form-icon-container i {
          font-size: 1.8rem;
          color: white;
        }

        .modern-input:focus {
          border-color: #28a745 !important;
          box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
          transform: translateY(-1px);
        }

        .modern-input:hover {
          border-color: #20c997;
        }

        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(40, 167, 69, 0.4) !important;
          background: linear-gradient(
            135deg,
            #218838 0%,
            #1e7e34 100%
          ) !important;
        }

        .login-btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }

        @media (max-width: 768px) {
          .auth-hero-section .display-4 {
            font-size: 2.5rem;
          }

          .auth-hero-section .py-5 {
            padding: 3rem 0 !important;
          }

          .auth-icon-container {
            width: 80px;
            height: 80px;
          }

          .auth-icon-container i {
            font-size: 2.5rem;
          }

          .login-card .p-5 {
            padding: 2rem !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Login;
